import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FormFilterSelect } from '@fs/zion-form'
import { PersonRemove } from '@fs/zion-icon'
import { Cell, IconButton, LabelSpacer, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { createRelationshipOptionGetter } from '../utils/relationshipOptions'

/**
 * One row of the Relationships card's edit mode: the relationship control for one group member,
 * labelled with their name, plus an inline control to remove them from the group.
 *
 * Red and the trash icon stay reserved for deleting a person, so removal uses `PersonRemove` and
 * neutral styling. It aligns to the top of the select, whose container reserves space for
 * validation text.
 *
 * Options are built here rather than passed in because they depend on this member: labels are
 * gendered by their sex.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.field - Field from useFieldArray
 * @param {string} props.field.id - Unique field identifier
 * @param {boolean} [props.field.isDeleted] - Whether the row is marked deleted
 * @param {number} props.index - This row's index in the form array
 * @param {string} props.memberName - The member's full name
 * @param {Object} [props.memberPerson] - The member's SLS PERSON; their sex genders the labels
 * @param {string} props.personName - Viewed person's name, for the control's accessible name
 * @param {Object} props.types - SLS types object
 * @param {Function} [props.onRemoveFromGroup] - Removes this member from the group; without it, no remove control
 * @returns {JSX.Element|null} The row, or null when marked deleted
 */
const RelationshipInputs = ({ field, index, memberName, memberPerson, personName, types, onRemoveFromGroup }) => {
  const [t] = useTranslation()

  const getRelationshipOptions = useMemo(
    () => createRelationshipOptionGetter(types, memberPerson),
    [types, memberPerson]
  )

  const handleRemove = useCallback(() => {
    onRemoveFromGroup(index)
  }, [onRemoveFromGroup, index])

  const removeLabel = useMemo(
    () => t('relationships-card.remove-from-group', 'Remove {personName} from group', { personName: memberName }),
    [t, memberName]
  )

  // Kept in the array so indexes stay consistent with the form state.
  if (field.isDeleted) {
    return null
  }

  return (
    <Row gutters="xxs" alignItemsY="top" wrap={false}>
      <FormFilterSelect
        growX
        name={`relationships[${index}].relationship`}
        data-testid={`familyMemberRelationshipField-${index}`}
        getOptions={getRelationshipOptions}
        label={memberName}
        aria-label={t(
          'relationships-card.member-relationship.aria-label',
          '{memberName} — relationship to {personName}',
          {
            memberName,
            personName,
          }
        )}
        largerLabel
        placeholder={t('relationships-card.filter-placeholder', 'Filter by relationship')}
        highlightMatchingText
        dense
      />
      {onRemoveFromGroup && (
        <Cell>
          <LabelSpacer largerLabel />
          <IconButton
            Icon={PersonRemove}
            onClick={handleRemove}
            data-testid={`remove-from-group-button-${index}`}
            title={removeLabel}
            aria-label={removeLabel}
          />
        </Cell>
      )}
    </Row>
  )
}

RelationshipInputs.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isDeleted: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  memberName: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  memberPerson: PropTypes.object,
  personName: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  types: PropTypes.object,
  onRemoveFromGroup: PropTypes.func,
}

RelationshipInputs.defaultProps = {
  memberName: '',
  memberPerson: undefined,
  personName: '',
  types: undefined,
  onRemoveFromGroup: undefined,
}

export default RelationshipInputs
